import { SearchParams } from '@/types/common';
import { ComponentType, lazy, RefObject } from 'react';
import type { TFunction } from 'i18next';
import type { TourProps } from 'antd/lib';
import { EventsActivityTypes, EventsEquippingLevels } from '@/constants/events';
import {
  myConveningReportingUrl,
  myCopReportingUrl,
  myEquippingReportingUrl,
  myHealingReportingUrl,
} from '@/utils/appUrls';
import { Event, Report } from '@/types/events';
import { ProfileData } from '@/types/currentUser';
import { CAME_FROM_LISTING_KEY } from '@/constants/common';
import Bugsnag from '@bugsnag/js';
import type { GetTokenSilentlyOptions } from '@auth0/auth0-react';
import { safeSessionStorage } from '@/utils/storage';

const AUTH0_TOKEN_RETRY_DELAYS_MS = [0, 1000, 3000];

const isAuth0TimeoutError = (error: unknown): boolean =>
  error instanceof Error && 'error' in error && error.error === 'timeout';

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const getAccessTokenWithTimeoutRetry = async (
  getAccessTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<string>,
  options: GetTokenSilentlyOptions,
): Promise<string> => {
  const lastAttempt = AUTH0_TOKEN_RETRY_DELAYS_MS.length - 1;

  for (let attempt = 0; ; attempt++) {
    const delayMs = AUTH0_TOKEN_RETRY_DELAYS_MS[attempt];
    if (delayMs) await wait(delayMs);

    try {
      return await getAccessTokenSilently(options);
    } catch (error) {
      if (attempt === lastAttempt || !isAuth0TimeoutError(error)) throw error;
    }
  }
};

export const consumeCameFromListing = (): boolean => {
  const cameFromListing = safeSessionStorage.getItem(CAME_FROM_LISTING_KEY) === '1';
  safeSessionStorage.removeItem(CAME_FROM_LISTING_KEY);

  return cameFromListing;
};

export const reportForbiddenAccessFromListing = (
  loaded: boolean,
  eventId: string,
  resource: string,
  cameFromListing: boolean,
) => {
  if (!loaded && cameFromListing) {
    Bugsnag.notify(new Error(`${resource} access denied (403) after navigation from listing`), (event) => {
      event.addMetadata('event', { eventId });
    });
  }
};

export const openInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

export const buildUrlWithParams = (basePath: string, path = '/', params?: SearchParams) => {
  const url = `${basePath}${path}`;
  if (!params) return url;

  const searchParams = Object.entries(params).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== '') {
      acc.append(key, value);
    }

    return acc;
  }, new URLSearchParams());

  return Array.from(searchParams).length ? `${url}?${searchParams}` : url;
};

export const pushSearchParams = (url: string) => window.history.pushState(null, '', url);
export const replaceSearchParams = (url: string) => window.history.replaceState(null, '', url);

export function debounce<Args extends unknown[]>(callback: (...args: Args) => void, delay: number) {
  let timer = 0;

  return (...args: Args) => {
    clearTimeout(timer);

    timer = window.setTimeout(() => {
      callback(...args);
    }, delay);
  };
}

export const prepareEmails = (string: string) => {
  const emailRegex = /\b[^\s@]+@([^\s@]+\.)+[A-Za-z]{2,}\b/g;

  const emails = string.match(emailRegex);

  return emails ? Array.from(new Set(emails)) : [];
};

export const lazyRetry = <T extends ComponentType<any>>(
  componentImport: Parameters<typeof lazy<T>>[0],
): Promise<{ default: T }> => {
  const sessionStorageKey = 'page-has-been-force-refreshed';

  return new Promise((resolve, reject) => {
    // check if the window has already been refreshed due to this chunk
    const hasRefreshed =
      typeof window !== 'undefined' ? JSON.parse(safeSessionStorage.getItem(sessionStorageKey) || 'false') : 'false';

    // try to import the component
    componentImport()
      .then((component) => {
        // success so reset the refresh state
        if (typeof window !== 'undefined') safeSessionStorage.setItem(sessionStorageKey, 'false');
        resolve(component);
      })
      .catch((error) => {
        if (typeof window === 'undefined') return;

        if (!hasRefreshed && safeSessionStorage.isAvailable()) {
          // not been refreshed yet
          safeSessionStorage.setItem(sessionStorageKey, 'true');
          // refresh the page
          return window.location.reload();
        }

        // Default error behaviour as already tried refresh
        reject(error);
      });
  });
};

export const getReportingUrl = (eventType: Event['event_type']) => {
  switch (eventType) {
    case EventsActivityTypes.healing:
      return myHealingReportingUrl(0);
    case EventsActivityTypes.equipping:
      return myEquippingReportingUrl(0);
    case EventsActivityTypes.cop:
      return myCopReportingUrl(0);
    case EventsActivityTypes.convening:
      return myConveningReportingUrl(0);
    default:
      throw new Error(`No activity type ${eventType}`);
  }
};

export const getFormattedData = (data: object) => {
  return Object.values(data)
    .filter((value) => typeof value === 'string' && value !== undefined)
    .join(', ');
};

export const isMobileAudioMaterialEvent = (event: Event) =>
  (event.material?.toLowerCase().includes('mobile audio') ?? false) ||
  (event.material_certification_area?.toLowerCase().includes('mobile audio') ?? false);

export const getTourTarget =
  (ref: RefObject<HTMLElement | null>, selector?: string): (() => HTMLElement) =>
  () =>
    ((selector ? ref.current?.closest(selector) : ref.current) ?? null) as HTMLElement;

export const getPageFaqTourStep = (
  t: TFunction,
  pageFaqLinkRef: RefObject<HTMLElement | null>,
): NonNullable<TourProps['steps']>[number] => ({
  title: t('common.tour.sidebar.steps.pageFaq.title'),
  description: t('common.tour.sidebar.steps.pageFaq.description'),
  target: getTourTarget(pageFaqLinkRef),
  placement: 'bottomLeft',
});

export const isMobileAudioEquippingEvent = (event: Pick<Event, 'event_type' | 'equipping_level'>) =>
  event.event_type === EventsActivityTypes.equipping && event.equipping_level === EventsEquippingLevels.mobileAudio;

export const isMobileAudioEvent = (event: Event) =>
  isMobileAudioEquippingEvent(event) || isMobileAudioMaterialEvent(event);

export type EventTypeTagKey =
  | 'healing'
  | 'equipping'
  | 'equippingInitial'
  | 'equippingAdvanced'
  | 'equippingSupplemental'
  | 'equippingMobileAudio'
  | 'convening'
  | 'cop';

export const getEventTypeTagKey = (
  type: EventsActivityTypes,
  level: EventsEquippingLevels | null | undefined,
): EventTypeTagKey => {
  if (type !== EventsActivityTypes.equipping || !level) {
    switch (type) {
      case EventsActivityTypes.healing:
        return 'healing';
      case EventsActivityTypes.equipping:
        return 'equipping';
      case EventsActivityTypes.convening:
        return 'convening';
      case EventsActivityTypes.cop:
        return 'cop';
    }
  }
  switch (level) {
    case EventsEquippingLevels.initial:
      return 'equippingInitial';
    case EventsEquippingLevels.advanced:
      return 'equippingAdvanced';
    case EventsEquippingLevels.supplemental:
      return 'equippingSupplemental';
    case EventsEquippingLevels.mobileAudio:
      return 'equippingMobileAudio';
  }
};

export const getEventTypeTagText = (
  t: TFunction,
  type: EventsActivityTypes,
  level: EventsEquippingLevels | null | undefined,
): string => {
  switch (getEventTypeTagKey(type, level)) {
    case 'healing':
      return t('eventsPage.tags.healing');
    case 'equipping':
      return t('eventsPage.tags.equippingInitial');
    case 'equippingInitial':
      return t('eventsPage.tags.equippingInitial');
    case 'equippingAdvanced':
      return t('eventsPage.tags.equippingAdvanced');
    case 'equippingSupplemental':
      return t('eventsPage.tags.equippingSupplemental');
    case 'equippingMobileAudio':
      return t('eventsPage.tags.equippingMobileAudio');
    case 'convening':
      return t('eventsPage.tags.convening');
    case 'cop':
      return t('eventsPage.tags.cop');
  }
};

export const getFilterPublicEventTypeLabel = (t: TFunction, activityTypeName: string): string => {
  switch (activityTypeName) {
    case EventsActivityTypes.healing:
      return t('eventsPage.filterPublicEvents.type.healing');
    case EventsActivityTypes.convening:
      return t('eventsPage.filterPublicEvents.type.convening');
    case EventsActivityTypes.cop:
      return t('eventsPage.filterPublicEvents.type.cop');
    default:
      return activityTypeName;
  }
};

export const getReportingTabLabel = (t: TFunction, activityTypeName: string): string => {
  switch (activityTypeName) {
    case EventsActivityTypes.healing:
      return t('myReportingPage.tabs.healing');
    case EventsActivityTypes.equipping:
      return t('myReportingPage.tabs.equipping');
    case EventsActivityTypes.convening:
      return t('myReportingPage.tabs.convening');
    case EventsActivityTypes.cop:
      return t('myReportingPage.tabs.cop');
    default:
      return activityTypeName;
  }
};

export const getFormattedManualLocation = (data: Event | Report | ProfileData) => {
  const manualLocation = {
    manual_location_country: data.manual_location_country,
    manual_location_state_province: data.manual_location_state_province,
    manual_location_city: data.manual_location_city,
    manual_location_street_address: data.manual_location_street_address,
    manual_location_zip: data.manual_location_zip,
  };

  return getFormattedData(manualLocation);
};

export const getSearchParams = (search: string, decode = true) => {
  const decodedUrl = decode ? decodeURI(search) : search;
  const params = new URLSearchParams(decodedUrl);

  return params;
};

export const updateURLParameter = (url: string, params: { [key: string]: string | number }, replaceUrl = true) => {
  const urlIns = new URL(url);

  Object.keys(params).forEach((key) => {
    const value = params[key].toString();

    if (urlIns.searchParams.has(key)) {
      urlIns.searchParams.set(key, value);
    } else {
      urlIns.searchParams.append(key, value);
    }
  });

  if (replaceUrl && window.history.replaceState) {
    window.history.replaceState(null, '', urlIns.href);
  }

  return urlIns.href;
};

export const removeURLParameter = (url: string, paramKey: string | string[], replaceUrl = true) => {
  const urlIns = new URL(url);

  if (Array.isArray(paramKey)) {
    paramKey.map((pKey) => urlIns.searchParams.delete(pKey));
  } else {
    urlIns.searchParams.delete(paramKey);
  }

  if (replaceUrl && window.history.replaceState) {
    window.history.replaceState(null, '', urlIns.href);
  }

  return urlIns.href;
};
